<template>
  <LoadingSpinner v-if="isLoading" />

  <div v-else-if="!isLoading && project && budget && changeOrder">
    <ProjectHeader :project="project" />
    <ProjectSubNavigation :project="project" />

    <!-- Change Order Form -->
    <div>
      <div>
        <!-- category input -->
        <form.Field
          name="name"
          :validators="{
            onBlur: yup.string().required('Category is required'),
          }"
        >
          <template #default="{ field }">
            <BaseInput
              :name="field.name"
              :model-value="category.name"
              placeholder="Category"
              class="mt-4"
              :error="
                field.state.meta.errors &&
                (field.state.meta.errors[0] as string)
              "
              @blur="
                updateBudgetItemsCategory(
                  $event,
                  category.name
                )
              "
            />
          </template>
        </form.Field>

        <!-- change order line items -->
        <!-- line items -->
        <BaseDraggable
          :model-value="sortedLineItems"
          item-key="id"
          :items-id-prefix="`id${category.key}`"
          @update:model-value="reOrderItems"
          @delete="(event) => deleteItem(event.element.id)"
        >
          <template #delete-button="{ element }">
            <span
              class="i-heroicons-trash text-error"
              :class="
                sortedLineItems.length === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-error cursor-pointer'
              "
              :disabled="sortedLineItems.length === 1"
              @click="sortedLineItems.length === 1
                  ? ''
                  : deleteItem(element.id)
              "
            />
          </template>
          <template #collapse-button="{ open, element, toggleFn }">
            <span
              class="text-gray-600 size-4"
              :class="
                open
                  ? 'i-mdi-unfold-less-horizontal'
                  : 'i-mdi-unfold-more-horizontal'
              "
              @click="
                toggleFn(element.id);
                validate(element.value, open)
              "
            />
          </template>

          <template #collapsed-text="{ element }">
            <div class="flex item-center py-2">
              <span class="font-bold">{{ element.value.title }}</span>
            </div>
          </template>

          <template #item-content="{ element: item, index }">
            <div :id="`id${category.key}${index}`">
              <CreateBudgetItem
                :id="item.value.id"
                v-model="item.value"
                :is-saving="false"
                @can-submit="
                  (value) => (itemsValidations[item.value.id] = value)
                "
                @on-delete="deleteItem(item.value.id)"
              />
            </div>
          </template>
        </BaseDraggable>

        <hr class="my-4" />
        <!-- add line buttons -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <BaseButton
              variant="base"
              type="button"
              class="w-full hover:!bg-gray-200"
              style="box-shadow: none !important"
              @click="addBudgetItem(category)"
            >
              <div class="flex items-center justify-center gap-2">
                <span class="i-heroicons-plus-circle size-8" />
                <span class="text-lg">Add line item</span>
              </div>
            </BaseButton>
          </div>

          <div>
            <BaseButton
              variant="base"
              type="button"
              class="w-full hover:!bg-gray-200"
              style="box-shadow: none !important"
              @click.prevent="showTemplatesModal(category)"
            >
              <div class="flex items-center justify-center gap-2">
                <span class="i-heroicons-plus-circle size-8" />
                <span class="text-lg">Add from template</span>
              </div>
            </BaseButton>
          </div>
        </div>

        <hr class="my-4" />
        <!-- save button -->
        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <div class="flex gap-4">
              <BaseButton
                :disabled="isSubmitting"
                text="Cancel"
                :class="{
                  'cursor-not-allowed opacity-50':isSubmitting,
                }"
                :to="{ name: 'ShowProposal', params: { id: proposalId } }"
              />

              <BaseButton
                :disabled="
                  !canSubmit ||
                  !isBudgetItemsFormsValid ||
                  isSubmitting
                "
                text="Save Changes"
                type="submit"
                variant="primary"
                :class="{
                  'cursor-not-allowed opacity-50':
                    !canSubmit ||
                    isSubmitting ||
                    !isBudgetItemsFormsValid
                }"
                @click="form.handleSubmit()"
              />
            </div>
          </template>
        </form.Subscribe>

      </div>
    </div>

    <!-- Budget items templates modal -->
    <Modal
      id="budgetTemplateModal"
      v-model="showModal"
      title="Apply Budget Template"
      :no-escape="true"
      :no-backdrop="true"
      :no-close-button="true"
      width="w-full md:w-[80%] lg:w-[70%] xl:w-[60%]"
    >
      <template #body>
        <BudgetItemTemplates
          @on-buget-template-selected="bugetTemplateSelected"
        />
      </template>
    </Modal>

  </div>

  <EmptyState v-else>
    <template #icon>
      <span class="i-heroicons-exclamation-circle text-red-500 size-8"></span>
    </template>
    <template #title>
      <h2 class="text-lg font-semibold text-center text-slate-900">Error</h2>
    </template>
    <template #description>
      <p class="text-sm text-center text-slate-500 pt-4">
        An error occurred while trying to load the change order.
      </p>
    </template>
  </EmptyState>
</template>

<script lang="ts" setup>
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ProjectSubNavigation from '@/components/project/ProjectSubNavigation.vue'
import ProjectHeader from '@/components/project/ProjectHeader.vue'
import { BudgetItem } from '@/models/budget'
import BudgetService from '@/services/BudgetService'
import ProjectService from '@/services/ProjectService'
import { useNotification } from '@kyvg/vue3-notification'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ChangeOrderService from '@/services/ChangeOrderService'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { ref, watch } from 'vue'
import { omit, uniqueId } from 'underscore'
import { toNumber } from '@/utils'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseDraggable from '@/components/base/BaseDraggable.vue'
import Modal from '@/components/app/AppModal.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import CreateBudgetItem from '@/modules/Budget/components/CreateBudgetItem.vue'
import BudgetItemTemplates from '@/modules/Budget/components/BudgetItemTemplates.vue'
import { Proposal } from '@/models/proposal'
import ProposalService from '@/services/ProposalService'
import { useHead } from '@unhead/vue'
import EmptyState from '@/components/app/EmptyState.vue'

useHead({ title: 'Edit Change Order' })

const $route = useRoute()
const proposalId = $route.params.proposalId as string || ''
const changeOrderId = $route.params.id as string || ''

const $router = useRouter()
const budgetService = new BudgetService()
const projectService = new ProjectService()
const changeOrderService = new ChangeOrderService(proposalId)
const queryClient = useQueryClient()
const { notify } = useNotification()

const { data: project, isLoading: projectLoading } = useQuery({
  queryKey: ['project', proposalId],
  queryFn: async () => projectService.find(proposalId),
})

const { data: budget, isLoading: budgetLoading } = useQuery({
  queryKey: ['budget', proposalId],
  queryFn: async () =>  {
    const budget = await budgetService.find(proposalId)
    const lineItems = await budgetService.getLineItems(proposalId)
    budget.lineItems = lineItems
    return budget
  },
})

const { data: changeOrder, isLoading: changeOrderLoading } = useQuery({
  queryKey: ['changeOrder', changeOrderId],
  queryFn: () => changeOrderService.find(changeOrderId),
})

const isLoading = computed(() => projectLoading.value || budgetLoading.value || changeOrderLoading.value)
const itemsValidations = ref({})
const showModal = ref(false)
const budgetTemplateCategory = ref(null)
const lineItems = ref([])
const deletedItems = ref([])

const category = ref({
  name: '',
  weight: 0,
  key: '',
})

const defaultValues = reactive({
  name: '',
})

watch((isLoading), (newVal) => {
  if (!newVal && changeOrder.value && budget.value) {
    lineItems.value = JSON.parse(JSON.stringify(budget.value.lineItems.filter((item) => changeOrder.value.lineItems.includes(item.id)).sort((a, b) => a.weight - b.weight)))
    category.value = {
      ...lineItems.value[0].category,
      key: uniqueId(),
    }
    defaultValues.name = category.value.name
  }
}, { immediate: true })


const sortedLineItems = computed(() => {
  return lineItems.value.map((item) => {
    return {
      value: item,
      id: item.id,
    }
  })
})

const form = useForm({
  defaultValues,
  validatorAdapter: yupValidator(),
  onSubmit: async () => {
    const payload = { changeOrder: changeOrder.value, lineItems: lineItems.value, deletedItems: deletedItems.value }
    await saveChangeOrder(payload)
  },
})

const updateBudgetItemsCategory = ($event, oldCategoryName) => {
  lineItems.value.forEach((item) => {
    if (item.category.name === oldCategoryName) {
      item.category.name = $event.target.value
      item.totalPrice =
        toNumber(item.totalPrice) === 0
          ? item.totalPrice === 0
            ? 0.0
            : 0
          : item.totalPrice
    }
  })
  form.useStore((state) => state.values.name = $event.target.value)
  form.validateField('name', 'blur')
  category.value.name = $event.target.value
}

const reOrderItems = (newItems) => {
  newItems.forEach((item, index) => {
    const budgetItem = lineItems.value.find(
      (lineItem) => lineItem.id === item.value.id,
    )
    budgetItem.weight = index + 1
  })
  lineItems.value = lineItems.value.sort((a, b) => a.weight - b.weight)
}

const deleteItem = (itemId) => {
  const index = lineItems.value.findIndex((item) => item.id === itemId)

  if (index > -1) {
    lineItems.value.splice(index, 1)
    itemsValidations.value = omit(itemsValidations.value, itemId)
    if (!itemId.includes('generatedID')) {
      deletedItems.value.push(itemId)
    }
  }
}

const nextLineItemWeight = (category) => {
  const items = lineItems.value.filter(
    (item) =>
      item.category.name === category.name &&
      item.category.weight === category.weight,
  )
  return items.length ? Math.max(...items.map((item) => item.weight)) + 1 : 1
}

const addBudgetItem = (category) => {
  const budgetItem = new BudgetItem()
  budgetItem.id = `${new Date().getTime()}-generatedID`
  budgetItem.materialsBudget = 0
  budgetItem.laborBudget = 0
  budgetItem.category = category
  budgetItem.weight = nextLineItemWeight(category)
  lineItems.value.push(Object.assign({}, budgetItem))
}

const validate = (item, open) => {
  if (open) {
    item.totalPrice =
      toNumber(item.totalPrice) === 0
        ? item.totalPrice === 0
          ? 0.0
          : 0
        : item.totalPrice
  }
}

const isBudgetItemsFormsValid = computed(() => {
  return Object.values(itemsValidations.value).every((value) => value)
})

const showTemplatesModal = (category) => {
  budgetTemplateCategory.value = category
  showModal.value = true
}

const bugetTemplateSelected = (items: BudgetItem[]) => {
  items.forEach((item) => {
    const budgetItem = Object.assign({}, JSON.parse(JSON.stringify(item)))
    budgetItem.id = `${new Date().getTime() + uniqueId()}-generatedID`
    budgetItem.category = budgetTemplateCategory.value
    budgetItem.weight = nextLineItemWeight(budgetTemplateCategory.value)
    lineItems.value.push(budgetItem)
  })
  showModal.value = false
}

const { mutateAsync: saveChangeOrder } = useMutation({
  mutationFn: async (payload: { changeOrder: Proposal, lineItems: BudgetItem[], deletedItems: string[] }) => {
    // delete items
    const deletePromises = []
    payload.deletedItems.forEach((itemId) => deletePromises.push(budgetService.deleteLineItem(proposalId, itemId)))
    await Promise.all(deletePromises)

    // update items
    const updateAddPromises = []
    payload.lineItems.forEach((item) => {
      if (payload.deletedItems.indexOf(item.id) > -1 || item.id.includes('generatedID')) {
        return
      }
      updateAddPromises.push(budgetService.updateLineItem(proposalId, item.id, item))
    })
    // add new items
    payload.lineItems.forEach((item) => {
      if (!item.id.includes('generatedID')) {
        return
      }
      updateAddPromises.push(budgetService.addLineItem(proposalId, item))
    })

    const updatedItems = await Promise.all(updateAddPromises)

    // update change order
    const changeOrderToUpdate = {
      ...payload.changeOrder,
      lineItems: updatedItems.map((item) => item.id),
    }

    return await new ProposalService().updateChangeOrder(proposalId, changeOrderToUpdate.id, changeOrderToUpdate)
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['changeOrders', proposalId] })
    queryClient.invalidateQueries({ queryKey: ['changeOrder', changeOrderId] })
    queryClient.invalidateQueries({ queryKey: ['budget', proposalId] })

    notify({
      type: 'success',
      title: 'Change Order updated',
      text: 'Change Order updated successfully',
    })
    $router.push({ name: 'ShowProposal', params: { id: proposalId } })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error updating change order',
      text: error.message || 'An error occurred while updating the change order',
    })
  },
})

</script>
